import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { IdentityDetailItem } from '@/components/business/expandable-identity-details'

/** 顶部 header 的页面级配置 (由具体页面通过 usePageHeader 上报) */
export interface PageHeaderState {
  /** 居中标题, 不传则不显示 (支持超长截断) */
  title?: string
  /** 标题内部链接, 适合在移动端页头进入当前对象详情 */
  titleHref?: string
  /** 标题下方的副标题/提示文案 */
  subtitle?: string
  /** 点击副标题后完整展示的结构化详情 */
  subtitleDetails?: IdentityDetailItem[]
  /** 返回回调, 存在时在菜单左侧显示返回箭头 (引用稳定, 由 usePageHeader 保证) */
  back?: () => void
}

/** setState 引用稳定, 单独成 context, 避免页面注册副作用因 state 变化反复触发 */
const PageHeaderSetContext = createContext<((state: PageHeaderState) => void) | null>(null)
const PageHeaderStateContext = createContext<PageHeaderState>({})

/**
 * 页面级 header 配置 Provider
 *
 * 设计: state / setState 拆成两个 context. setState 引用稳定, 页面通过 usePageHeader
 * 上报标题/副标题/返回, 顶部布局通过 usePageHeaderState 读取并渲染到 AppHeader.
 */
export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PageHeaderState>({})
  return (
    <PageHeaderSetContext.Provider value={setState}>
      <PageHeaderStateContext.Provider value={state}>{children}</PageHeaderStateContext.Provider>
    </PageHeaderSetContext.Provider>
  )
}

/** 顶部布局读取当前页面 header 配置 */
export function usePageHeaderState(): PageHeaderState {
  return useContext(PageHeaderStateContext)
}

export interface UsePageHeaderConfig {
  title?: string
  titleHref?: string
  subtitle?: string
  subtitleDetails?: IdentityDetailItem[]
  onBack?: () => void
}

/**
 * 页面调用: 把标题/副标题/返回行为上报给顶部 AppHeader.
 * 组件卸载时自动清空, 避免标题残留到下一个页面.
 */
export function usePageHeader({
  title,
  titleHref,
  subtitle,
  subtitleDetails,
  onBack,
}: UsePageHeaderConfig): void {
  const setState = useContext(PageHeaderSetContext)

  // onBack 通常是内联函数, 用本地 ref 持有最新值, 对外暴露稳定回调, 避免每次渲染重复 setState
  const onBackRef = useRef(onBack)
  useEffect(() => {
    onBackRef.current = onBack
  })
  const stableBack = useCallback(() => onBackRef.current?.(), [])

  const hasBack = Boolean(onBack)
  useEffect(() => {
    if (!setState) return
    setState({
      title,
      titleHref,
      subtitle,
      subtitleDetails,
      back: hasBack ? stableBack : undefined,
    })
    return () => setState({})
  }, [setState, title, titleHref, subtitle, subtitleDetails, hasBack, stableBack])
}
